<script setup lang="ts">
import { useDisplay } from 'vuetify'
import { useRouter } from 'vue-router'
import { useI18nFallback } from '@/composable/useI18nFallback'
import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'
import { useUpdateAcademicYearAcknowledgement } from '@/composable/queries/Dara/organisationAcademicYears'
import AcademicYearWelcomeModalImage from '@images/modals/WelcomeModalSide.jpg'


const appStore = useAppStore()
const authStore = useAuthStore()
const { lgAndUp } = useDisplay()
const router = useRouter()
const { t } = useI18nFallback()

const dontShowAgain = ref(false)

// Move composable to top level - need to handle undefined case
const updateAcknowledgement = useUpdateAcademicYearAcknowledgement(
  computed(() => authStore.currentAcademicYear?.id || 0),
)

const handleClose = () => {
  if (!dontShowAgain.value) {
    appStore.academicYearWelcomeModal.active = false

    return
  }

  updateAcknowledgement.mutate(
    undefined,
    {
      onSuccess: () => {
        appStore.academicYearWelcomeModal.shouldDisplay = false
        appStore.academicYearWelcomeModal.active = false
      },
      onError: () => {
        appStore.setSnackbar({
          colour: 'error',
          text: 'Failed to acknowledge Academic Year',
        })
      },
    },
  )
}

const navigateToRoute = (routeName: string) => {
  appStore.academicYearWelcomeModal.active = false

  router.push({ name: routeName })
}
</script>

<template>
  <VDialog
    v-model="appStore.academicYearWelcomeModal.active"
    width="auto"
    max-height="90vh"
    max-width="70rem"
  >
    <VCard class="pa-0 academic-year-welcome-modal">
      <VRow
        no-gutters
        class="ma-0"
      >
        <VCol
          cols="12"
          lg="8"
          class="pa-6"
        >
          <h2 class="mb-4">
            {{ t('pages.dashboard.new_academic_year_modal.title', { academicYearLabel: authStore.currentAcademicYear?.label }) }}
          </h2>
          <p
            v-dompurify-html="t('pages.dashboard.new_academic_year_modal.text1')"
            class="mb-8"
          />
          <div class="academic-year-welcome-modal-list mb-4">
            <div
              class="d-flex pa-4 academic-year-welcome-modal-list__stripe cursor-pointer"
              @click="navigateToRoute('organisation.subjects-and-qualifications')"
            >
              <div class="academic-year-welcome-modal-list__number mr-4">
                1
              </div>
              <p v-dompurify-html="t('pages.dashboard.new_academic_year_modal.text2')" />
            </div>
            <div
              class="d-flex pa-4 cursor-pointer"
              @click="navigateToRoute('organisation.year-groups.list')"
            >
              <div class="academic-year-welcome-modal-list__number mr-4">
                2
              </div>
              <p v-dompurify-html="t('pages.dashboard.new_academic_year_modal.text3')" />
            </div>
            <div
              class="d-flex pa-4 academic-year-welcome-modal-list__stripe cursor-pointer"
              @click="navigateToRoute('students.add-options')"
            >
              <div class="academic-year-welcome-modal-list__number mr-4">
                3
              </div>
              <p v-dompurify-html="t('pages.dashboard.new_academic_year_modal.text4')" />
            </div>
            <div
              class="d-flex pa-4 cursor-pointer"
              @click="navigateToRoute('organisation.staff.list')"
            >
              <div class="academic-year-welcome-modal-list__number mr-4">
                4
              </div>
              <p v-dompurify-html="t('pages.dashboard.new_academic_year_modal.text5')" />
            </div>
          </div>
          <p
            v-dompurify-html="t('pages.dashboard.new_academic_year_modal.text6')"
            class="mb-4"
          />
          <div class="d-flex align-center justify-space-between mx-2 border-t pt-4">
            <VCheckbox
              v-model="dontShowAgain"
              :label="t('pages.dashboard.new_academic_year_modal.dont_show_again')"
              density="compact"
              hide-details
            />
            <VBtn
              color="primary"
              variant="elevated"
              class="text-none"
              :loading="updateAcknowledgement.isPending.value"
              @click="handleClose"
            >
              {{ t('actions.close') }}
            </VBtn>
          </div>
        </VCol>
        <VCol
          v-if="lgAndUp"
          class="ma-0 pa-0"
          cols="12"
          lg="4"
        >
          <VImg
            class="h-100"
            :src="AcademicYearWelcomeModalImage"
            :alt="t('pages.dashboard.new_academic_year_modal.title', { academicYearLabel: authStore.currentAcademicYear?.label })"
            width="100%"
            cover
          />
        </VCol>
      </VRow>
    </VCard>
  </VDialog>
</template>

<style lang="scss">
.academic-year-welcome-modal {
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.3) !important;
}

.academic-year-welcome-modal-list {
  background-color: rgb(var(--v-theme-background));

  &__number {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background-color: rgb(var(--v-theme-secondary));
    color: white;
    border-radius: 50%;
    font-size: 14px;
    font-weight: 600;
    flex-shrink: 0;
  }

  a {
    color: rgb(var(--v-theme-on-surface));
  }

  .cursor-pointer {
    transition: background-color 0.2s ease;

    &:hover {
      background-color: rgba(var(--v-theme-primary), 0.15);
    }
  }
}

.code {
  background-color: rgb(var(--v-theme-code));
  color: rgb(var(--v-theme-on-code));
  border-radius: 4px;
  line-height: 1.8;
  font-size: .9em;
  font-weight: 400;
  padding: .2em .4em;
}
</style>
